import { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import type {
  PositionResponse,
  SuggestionStateResponse,
} from "@patent-studio/api-types";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";
import { Panel } from "@/components/ui/Panel";
import { Spinner } from "@/components/ui/Spinner";
import { StatusPill } from "@/components/ui/StatusPill";
import { refusalDetail } from "@/services/api-client";
import { useInventionElements } from "@/hooks/use-invention";
import {
  useAddPosition,
  useAmendPosition,
  usePositions,
  useRemovePosition,
  useSuggestPositions,
} from "@/hooks/use-positions";
import { mappableElements } from "./element-mapping";
import { NewPositionForm } from "./NewPositionForm";
import { PositionRow } from "./PositionRow";
import { useChangesOn } from "../../notifications/StepChanges";
import { RemoveConfirmation } from "./RemoveConfirmation";

/**
 * The two ways a position comes to exist, as the design's pair of tinted
 * cards: the attorney drafts one, or asks the tool for some.
 */
function ActionCard({
  tone,
  icon,
  title,
  blurb,
  disabled,
  note,
  onClick,
}: {
  tone: "brand" | "accent";
  icon: React.ReactNode;
  title: string;
  /** Shown while the list is empty, when the attorney has yet to learn what each does. */
  blurb: string | null;
  disabled?: boolean;
  note?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex flex-col gap-1 rounded-md border px-4 text-left transition-colors",
        blurb === null ? "py-3" : "py-3.5",
        tone === "brand"
          ? "border-primary/10 bg-primary/[0.06] text-primary hover:bg-primary/[0.1]"
          : "border-brand-end/15 bg-brand-end/[0.06] text-brand-end hover:bg-brand-end/[0.1]",
        disabled
          ? "cursor-not-allowed opacity-70 hover:bg-transparent"
          : "cursor-pointer",
      )}
    >
      <span
        className={cn(
          "flex items-center gap-2 text-[13.5px] font-bold",
          blurb === null && "justify-center",
        )}
      >
        {icon}
        {title}
        {note !== undefined && (
          <StatusPill tone="inherit" size="sm">
            {note}
          </StatusPill>
        )}
      </span>
      {blurb !== null && (
        <span className="text-[12px] font-normal text-muted-foreground">
          {blurb}
        </span>
      )}
    </button>
  );
}

function Actions({
  empty,
  suggesting,
  onAdd,
  onSuggest,
}: {
  empty: boolean;
  /** A run is in flight: asked for, or served as running. Both cards hold until it lands. */
  suggesting: boolean;
  onAdd: () => void;
  onSuggest: () => void;
}) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      <ActionCard
        tone="brand"
        icon={<Plus className="h-4 w-4" aria-hidden />}
        title="Add new position"
        blurb={
          empty
            ? "Draft a position yourself and map it to the invention elements it rests on."
            : null
        }
        disabled={suggesting}
        onClick={onAdd}
      />
      {suggesting ? (
        <ActionCard
          tone="accent"
          icon={<Spinner />}
          title="Suggesting positions…"
          blurb="Praxis is reviewing the locked invention and its agreed elements…"
          disabled
        />
      ) : (
        <ActionCard
          tone="accent"
          icon={<Sparkles className="h-4 w-4" aria-hidden />}
          title={empty ? "Suggest positions" : "Suggest additional positions"}
          blurb={
            empty
              ? "Let Praxis suggest positions based on the agreed elements."
              : null
          }
          onClick={onSuggest}
        />
      )}
    </div>
  );
}

/**
 * What the last run came to, under the cards: how many it added, or that
 * every proposal was already in the list, or the server's words when it
 * failed. A refusal of the ask itself is shown the same way; the card is
 * enabled again either way, since running again is always allowed.
 */
function RunOutcome({
  run,
  refusal,
}: {
  run: SuggestionStateResponse;
  refusal: string | null;
}) {
  const failure = refusal ?? (run.status === "failed" ? run.error : null);
  if (failure !== null) {
    return (
      <Panel
        as="p"
        shape="row"
        pad="tight"
        tone="destructive"
        role="alert"
        className="mt-2 text-[11.5px]"
      >
        {failure}
      </Panel>
    );
  }
  if (run.status !== "complete") return null;
  const added = run.added_position_ids.length;
  return (
    <p role="status" className="mt-2 text-[11.5px] text-muted-foreground">
      {added === 0
        ? "Nothing new to suggest: every suggestion is already in your list."
        : `${plural(added, "position")} suggested. Amend or remove each one like any other.`}
    </p>
  );
}

/** The ask's own refusal, in the server's words, or nothing while it has not been refused. */
function refusalOf(suggest: {
  isError: boolean;
  error: unknown;
}): string | null {
  return suggest.isError
    ? refusalDetail(suggest.error, "Positions could not be suggested.")
    : null;
}

/**
 * The Define Positions panel: the attorney's arguments for novelty, drafted
 * here before the analysis runs. Each is confirmed inside the analysis, one at
 * a time.
 *
 * Every write answers the whole set, so the list is always what the server
 * holds.
 */
export function PositionsPanel({ taskId }: { taskId: string }) {
  const { data } = usePositions(taskId);
  const { data: invention } = useInventionElements(taskId);
  const add = useAddPosition(taskId);
  const amend = useAmendPosition(taskId);
  const remove = useRemovePosition(taskId);
  const suggest = useSuggestPositions(taskId);
  const changeOn = useChangesOn(taskId, "define_positions");
  const [drafting, setDrafting] = useState(false);
  const [removing, setRemoving] = useState<PositionResponse | null>(null);

  const positions = data?.positions ?? [];
  const elements = mappableElements(invention);
  const suggesting = suggest.isPending || data?.suggestion.status === "running";
  const busy =
    add.isPending || amend.isPending || remove.isPending || suggesting;

  return (
    <div data-testid="positions-panel">
      {positions.length > 0 && (
        <ul>
          {positions.map((position) => (
            <PositionRow
              key={position.position_id}
              position={position}
              elements={elements}
              busy={busy}
              suggestion={changeOn(position.position_id)}
              actions={{
                onAmend: (positionId, body) =>
                  amend.mutateAsync({ positionId, body }),
                onRemove: setRemoving,
              }}
            />
          ))}
        </ul>
      )}
      {drafting ? (
        <NewPositionForm
          elements={elements}
          busy={busy}
          onAdd={(body) => add.mutateAsync(body)}
          onDone={() => setDrafting(false)}
        />
      ) : (
        <div className="mt-3">
          <Actions
            empty={positions.length === 0}
            suggesting={suggesting}
            onAdd={() => setDrafting(true)}
            onSuggest={() => suggest.mutate()}
          />
          {data !== undefined && (
            <RunOutcome run={data.suggestion} refusal={refusalOf(suggest)} />
          )}
        </div>
      )}
      <RemoveConfirmation
        position={removing}
        onCancel={() => setRemoving(null)}
        onConfirm={(position) => {
          setRemoving(null);
          remove.mutate(position.position_id);
        }}
      />
    </div>
  );
}
